import { useState, useCallback, useEffect } from "react";
import measurementFieldConfigService from "../services/measurementFieldConfigService";
import toast from "react-hot-toast";

export const useMeasurementFieldConfigs = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null); // null = new category
  const [categoryFields, setCategoryFields] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchCategories = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await measurementFieldConfigService.getGarmentCategories();
      setCategories(response.data.categories || []);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to fetch garment categories"
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchFieldsForCategory = useCallback(async (garmentCategory) => {
    try {
      const response = await measurementFieldConfigService.getAll({
        garmentCategory,
      });
      return response.data.configs || [];
    } catch (error) {
      toast.error("Failed to fetch fields for category");
      return [];
    }
  }, []);

  // Open modal to set up (or edit) a garment category's fields
  const handleOpenSetup = useCallback(
    async (categoryName = null) => {
      setEditingCategory(categoryName);
      if (categoryName) {
        const fields = await fetchFieldsForCategory(categoryName);
        setCategoryFields(
          fields.map((f) => ({
            fieldKey: f.fieldKey,
            label: f.label,
            unit: f.unit || "inches",
            sortOrder: f.sortOrder ?? 0,
          }))
        );
      } else {
        // New category — start with one empty field row
        setCategoryFields([
          { fieldKey: "", label: "", unit: "inches", sortOrder: 0 },
        ]);
      }
      setShowModal(true);
    },
    [fetchFieldsForCategory]
  );

  // Save all fields for a category (bulkCreate replaces existing ones)
  const handleSaveCategory = useCallback(
    async (garmentCategory, fields) => {
      setIsSaving(true);
      try {
        await measurementFieldConfigService.bulkCreate({
          garmentCategory,
          fields,
        });
        toast.success(`Fields saved for "${garmentCategory}"`);
        setShowModal(false);
        setEditingCategory(null);
        setCategoryFields([]);
        await fetchCategories();
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to save fields"
        );
      } finally {
        setIsSaving(false);
      }
    },
    [fetchCategories]
  );

  // Delete entire category — removes all field configs AND all customer measurements
  const handleDeleteCategory = useCallback(
    async (garmentCategory) => {
      if (
        !window.confirm(
          `Delete "${garmentCategory}"? This will remove all field configs and all customer measurements for this category. This cannot be undone.`
        )
      )
        return;

      setIsDeleting(true);
      try {
        await measurementFieldConfigService.deleteCategory(garmentCategory);
        toast.success(`"${garmentCategory}" and all its measurements deleted`);
        await fetchCategories();
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to delete category");
      } finally {
        setIsDeleting(false);
      }
    },
    [fetchCategories]
  );

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingCategory(null);
    setCategoryFields([]);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, []);

  return {
    categories,
    isLoading,
    showModal,
    editingCategory,
    categoryFields,
    setCategoryFields,
    isSaving,
    isDeleting,
    handleOpenSetup,
    handleSaveCategory,
    handleDeleteCategory,
    handleCloseModal,
    fetchCategories,
  };
};

export default useMeasurementFieldConfigs;
